import { SUPPORTED_LOCALES } from '@platform/i18n';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  acknowledgeProtocol,
  bookOnCall,
  type CurrentUser,
  cancelShift,
  crisisProtocol,
  type OnCallWindow,
  type ProtocolState,
  type SupervisionThread,
  type SupervisionView,
  saveCrisisProtocol,
  sendSupervisionMessage,
  supervisionThread,
  supervisionThreads,
} from '../api.ts';
import { formatWhen } from '../format.ts';
import { type Tone, useAnnounce } from '../hooks/useAnnounce.tsx';
import { apiMessage } from '../messages.ts';
import { cloneSteps, type ProtocolSteps, withoutStep, withStep } from '../protocol-steps.ts';

/**
 * Supervisione e protocollo di crisi (sezione 5.9, mock 2 `sup-view` e mock 3).
 *
 * Cosa vede un supervisore: **che** una sessione è in corso, da quanto, su
 * quale canale, e i messaggi del volontario. Mai il contenuto della
 * conversazione, nemmeno in forma di trascrizione o riassunto — e qui non c'è
 * nemmeno da rinunciarci, perché quel contenuto non esiste. Nessun percorso di
 * questa schermata porta dentro una room (vincolo 4).
 *
 * Il **protocollo di crisi** vive qui e non in una schermata sua: è la stessa
 * domanda vista dai due lati — cosa fa un volontario quando non c'è nessuno —
 * e chi coordina lo scrive nel posto in cui guarda le richieste che quel
 * protocollo dovrà coprire.
 *
 * La presa visione è per tutti, coordinatori compresi: "un volontario senza ack
 * sulla versione corrente non entra in turno", e chi coordina va in turno come
 * gli altri.
 */

const THREAD_POLL_MS = 4000;

type Props = {
  user: CurrentUser;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
};

export function SupervisionPage({ user, headingRef }: Props) {
  const { t, i18n } = useTranslation();

  /**
   * Tutto quello che arriva dalla stessa lettura, e che prima di quella lettura
   * **non si sa**.
   *
   * `myOnCall` e `coverage` nascevano `[]`: la schermata diceva "non hai
   * dichiarato nessuna reperibilità" e "nessuna reperibilità dichiarata" —
   * quest'ultima in un banner d'avviso — mentre la richiesta era ancora in
   * volo. Erano due affermazioni false, e una delle due era la frase che dice
   * a un coordinatore che il servizio è scoperto. `undefined` finché non si sa,
   * come `threads`.
   */
  const [threads, setThreads] = useState<SupervisionThread[]>();
  const [onCall, setOnCall] = useState<{ until: string } | null>(null);
  const [myOnCall, setMyOnCall] = useState<OnCallWindow[]>();
  const [coverage, setCoverage] =
    useState<{ startsAt: string; endsAt: string; displayName: string | null }[]>();
  /**
   * La lettura è fallita, che non è "non c'è niente".
   *
   * Prima il fallimento scriveva `setThreads([])`: il banner rosso diceva che
   * non si era riusciti a leggere e, sotto, "Nessuna richiesta." diceva che non
   * ce n'erano. Due risposte diverse alla stessa domanda nella stessa
   * schermata, e quella grande era la falsa.
   */
  const [failed, setFailed] = useState(false);
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [savingWindow, setSavingWindow] = useState(false);
  const [openThread, setOpenThread] = useState<SupervisionView>();
  const [draft, setDraft] = useState('');
  /**
   * Anche qui la regione live e il banner sono **lo stesso nodo**.
   *
   * Questa schermata montava la regione insieme al messaggio, come la
   * moderazione: una regione che compare già piena non viene annunciata, e chi
   * supervisiona senza vedere dichiarava una reperibilità senza sentire se era
   * stata registrata (AJ15, AO1).
   */
  const { say, announcer } = useAnnounce();
  const [protocol, setProtocol] = useState<ProtocolState>();
  const threadHeadingRef = useRef<HTMLHeadingElement>(null);
  const threadBoxRef = useRef<HTMLDivElement>(null);

  const isSupervisor = user.role === 'supervisor' || user.role === 'coordinator';
  /**
   * La lingua in cui il protocollo va scritto **almeno**.
   *
   * È quella dell'ente: è su quella che ripiega chi legge in una lingua non
   * tradotta, ed è l'unica che il salvataggio pretende. La console non la
   * conosce ancora — arriva con la configurazione dell'ente, fase 7 — quindi
   * per ora si comincia dalla lingua predefinita del prodotto.
   */
  const tenantLocale = SUPPORTED_LOCALES[0];

  const loadThreads = useCallback(async () => {
    if (!isSupervisor) return;
    try {
      const result = await supervisionThreads();
      setThreads(result.threads);
      setOnCall(result.onCall);
      setMyOnCall(result.myOnCall);
      setCoverage(result.coverage ?? []);
      setFailed(false);
    } catch {
      setFailed(true);
      say(t('supervision.load_failed'), 'danger');
    }
  }, [isSupervisor, say, t]);

  const loadProtocol = useCallback(async () => {
    try {
      setProtocol(await crisisProtocol());
    } catch {
      say(t('protocol.load_failed'), 'danger');
    }
  }, [say, t]);

  useEffect(() => {
    void loadThreads();
    void loadProtocol();
  }, [loadThreads, loadProtocol]);

  /** Il thread aperto si rilegge da solo: la risposta arriva mentre si guarda. */
  const openSessionId = openThread?.sessionId;
  useEffect(() => {
    if (!openSessionId) return;
    /**
     * Il segnale di annullamento che qui non c'era.
     *
     * La pulizia toglieva solo l'intervallo, e una lettura già in volo non ne
     * sapeva niente: premendo "Torna all'elenco" mentre un giro era partito, la
     * risposta arrivava dopo e riscriveva `openThread` — il thread appena
     * chiuso si riapriva da solo, l'elenco e la reperibilità sparivano e il
     * fuoco veniva strappato a chi era appena tornato indietro. È la stessa
     * difesa che usano già `ActiveCall` per il contesto della sessione e
     * `Debrief` per la nota: il risultato di una lettura annullata non tocca
     * niente.
     */
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const result = await supervisionThread(openSessionId);
        if (cancelled) return;
        setOpenThread(result);
      } catch {
        // Un giro perso non è il thread perso.
      }
    };
    const handle = setInterval(() => void poll(), THREAD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [openSessionId]);

  /**
   * Il fuoco entra nel thread **quando si apre**, non a ogni giro.
   *
   * Dipendendo dall'oggetto, l'interrogazione periodica lo strappava dalla
   * casella di risposta ogni quattro secondi mentre il supervisore scriveva —
   * la stessa trappola di X3, in una schermata nuova.
   */
  useEffect(() => {
    if (openSessionId) threadHeadingRef.current?.focus();
  }, [openSessionId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `openThread` non si legge nel corpo, ma i messaggi nuovi devono restare visibili invece di finire sotto il bordo.
  useEffect(() => {
    const node = threadBoxRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [openThread]);

  /**
   * Chiudendo un thread il fuoco torna all'elenco, non su `body`.
   *
   * `listHeadingRef.current?.focus()` veniva chiamato **dentro il gestore del
   * clic**, cioè prima che React avesse ridisegnato: il titolo dell'elenco
   * esiste solo quando `openThread` è vuoto, quindi in quel momento non era
   * ancora montato, il `?.` ingoiava la chiamata e il fuoco restava su `body` —
   * chi torna indietro da tastiera ripartiva dall'inizio del documento (WCAG
   * 2.4.3). L'intenzione si annota qui e si esegue dopo il ridisegno, come per
   * il fuoco delle fasce in `ShiftsPage`.
   */
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const returningToList = useRef(false);

  useEffect(() => {
    if (openThread || !returningToList.current) return;
    returningToList.current = false;
    listHeadingRef.current?.focus();
  }, [openThread]);

  /**
   * Una fascia dichiarata dal fuso del browser.
   *
   * `datetime-local` non porta un fuso: quello che la persona scrive è l'ora
   * che legge sull'orologio davanti a sé, ed è quella che il browser converte
   * in un istante. È l'unico punto del prodotto in cui il fuso giusto è quello
   * di chi guarda e non quello dell'ente — perché a rispondere al telefono è
   * lei, non l'ente.
   */
  const declareOnCall = async (): Promise<void> => {
    if (savingWindow) return;
    setSavingWindow(true);
    try {
      await bookOnCall({
        startsAt: new Date(windowStart).toISOString(),
        endsAt: new Date(windowEnd).toISOString(),
      });
      say(t('on_call.added'));
      setWindowStart('');
      setWindowEnd('');
      await loadThreads();
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('on_call.add_failed')} ${apiMessage(t, key)}`, 'danger');
    } finally {
      setSavingWindow(false);
    }
  };

  const dropOnCall = async (window: OnCallWindow): Promise<void> => {
    try {
      await cancelShift(window.id);
      say(t('on_call.removed'));
      await loadThreads();
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('on_call.remove_failed')} ${apiMessage(t, key)}`, 'danger');
    }
  };

  const open = async (sessionId: string): Promise<void> => {
    try {
      setOpenThread(await supervisionThread(sessionId));
      // Il thread è aperto: l'errore di prima non è più vero, e un messaggio
      // vuoto toglie il banner senza lasciare niente da rileggere.
      say('');
    } catch {
      say(t('supervision.load_failed'), 'danger');
    }
  };

  const reply = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || !openThread) return;
    setDraft('');
    try {
      await sendSupervisionMessage(openThread.sessionId, body);
      setOpenThread(await supervisionThread(openThread.sessionId));
      void loadThreads();
    } catch {
      setDraft(body);
      say(t('supervision.not_sent'), 'danger');
    }
  };

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('supervision.title')}
      </h2>

      {announcer}

      {/*
        Il nodo resta montato e **vuoto** finché non si sa: una regione live che
        nasce piena non viene annunciata, e questa nasceva dicendo "Non sei in
        reperibilità adesso" a chi lo era.
      */}
      {isSupervisor ? (
        <p className="muted" role="status">
          {threads === undefined
            ? ''
            : onCall
              ? t('supervision.on_call', {
                  when: formatWhen(onCall.until, i18n.resolvedLanguage, user.tenant?.timezone),
                })
              : t('supervision.off_call')}
        </p>
      ) : null}

      {openThread ? (
        <section className="card" aria-labelledby="thread-heading">
          <h3 id="thread-heading" ref={threadHeadingRef} tabIndex={-1}>
            {openThread.volunteer?.displayName ?? t('supervision.from_volunteer')}
          </h3>
          <p className="muted">
            {[
              openThread.session?.anonymous
                ? t('supervision.anonymous')
                : t('supervision.registered'),
              openThread.session?.live ? t('supervision.live') : t('supervision.ended'),
              t('supervision.started_at', {
                when: formatWhen(
                  openThread.startedAt,
                  i18n.resolvedLanguage,
                  user.tenant?.timezone,
                ),
              }),
            ].join(' · ')}
          </p>
          {/*
            Ciò che manca non è nascosto: non esiste. Va detto, altrimenti chi
            legge cerca un pulsante che non troverà (sezione 5.9).
          */}
          <p className="muted">{t('supervision.no_conversation_note')}</p>

          <div
            ref={threadBoxRef}
            className="chat__thread"
            role="log"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: un riquadro che scorre deve essere raggiungibile da tastiera (WCAG 2.1.1).
            tabIndex={0}
            aria-label={t('supervision.thread_label')}
          >
            {/*
              Il motivo dell'allerta apre il thread, e sta **dentro** il thread.

              Era un paragrafo sciolto sopra il riquadro: la stessa frase che
              il volontario ha scritto, ma senza autore, senza posto nella
              conversazione e — dall'altra parte — invisibile. Qui è il primo
              messaggio, con la stessa forma di tutti gli altri, così le due
              schermate mostrano la stessa cosa nello stesso ordine.
            */}
            {openThread.reason ? (
              <p className="chat__msg chat__msg--them">
                <span className="visually-hidden">{`${t('supervision.from_volunteer')}: `}</span>
                {openThread.reason}
              </p>
            ) : null}
            {openThread.messages.map((message) => (
              <p
                key={message.id}
                className={`chat__msg chat__msg--${
                  message.sender === 'supervisor' ? 'me' : 'them'
                }`}
              >
                <span className="visually-hidden">{`${t(
                  message.sender === 'supervisor'
                    ? 'supervision.from_supervisor'
                    : 'supervision.from_volunteer',
                )}: `}</span>
                {message.body}
              </p>
            ))}
          </div>

          <form className="chat__composer" onSubmit={(event) => void reply(event)}>
            <label className="visually-hidden" htmlFor="supervision-reply">
              {t('supervision.write_label')}
            </label>
            <textarea
              id="supervision-reply"
              rows={2}
              maxLength={2000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('supervision.write_label')}
            />
            <button type="submit" className="button button--primary">
              {t('supervision.send')}
            </button>
          </form>

          <button
            type="button"
            className="button button--wide"
            onClick={() => {
              returningToList.current = true;
              setOpenThread(undefined);
            }}
          >
            {t('supervision.back')}
          </button>
        </section>
      ) : null}

      {/*
        Le richieste per prime, la reperibilità sotto.

        Erano nell'ordine opposto: il modulo per dichiararsi reperibili — una
        decisione che si prende di rado, con due caselle di data e ora — stava
        **sopra** l'elenco delle richieste, cioè sopra la cosa per cui questa
        schermata si apre ogni volta. Chi supervisiona apriva la pagina e
        trovava un modulo.
      */}
      {isSupervisor && !openThread ? (
        <section className="card" aria-labelledby="threads-heading">
          <div className="section-head">
            {/*
              Non "Supervisione" un'altra volta: era la stessa identica parola
              del titolo della pagina, su due livelli diversi nella stessa
              schermata. Il titolo di una scheda deve dire cosa c'è **dentro**.
            */}
            <h3 id="threads-heading" ref={listHeadingRef} tabIndex={-1}>
              {t('supervision.requests_title')}
            </h3>
            {threads === undefined || threads.length === 0 ? null : (
              <p className="count">{t('supervision.requests_count', { count: threads.length })}</p>
            )}
          </div>
          {/*
            Non dietro l'icona: conferma il vincolo 4 (nessuna registrazione,
            nessun accesso alle conversazioni) prima dell'elenco delle
            richieste — stesso criterio di `moderation.on_volunteers_hint` in
            BQ2 (`docs/DECISIONI.md`).
          */}
          <p className="card__sub">{t('supervision.description')}</p>

          {failed ? (
            /* Un fallimento non è un elenco vuoto: il perché lo dice il banner
               d'esito, qui c'è il modo di riprovare. */
            <p>
              <button type="button" className="button" onClick={() => void loadThreads()}>
                {t('supervision.retry')}
              </button>
            </p>
          ) : threads === undefined ? (
            <>
              <p className="visually-hidden">{t('supervision.loading')}</p>
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--half" aria-hidden="true" />
            </>
          ) : threads.length === 0 ? (
            <p className="empty">{t('supervision.empty')}</p>
          ) : (
            <ul className="plain-list">
              {threads.map((thread) => (
                <li key={thread.supervisionId} className="row">
                  <div>
                    <p className="row__title">
                      {thread.volunteer?.displayName ?? t('supervision.from_volunteer')}
                    </p>
                    <p className="muted">
                      {[
                        thread.session?.anonymous
                          ? t('supervision.anonymous')
                          : t('supervision.registered'),
                        thread.session?.live ? t('supervision.live') : t('supervision.ended'),
                      ].join(' · ')}
                    </p>
                  </div>
                  <div className="row__actions">
                    {/*
                      Lo stato ha un testo suo, non la stringa della sessione:
                      distinguere "presa in carico" da "da rileggere" con il
                      solo colore è WCAG 1.4.1.

                      E i due colori sono **questi**, non gli altri. Prima "da
                      rileggere" — cioè una richiesta che nessuno ha ancora
                      preso — era verde, e "presa in carico" grigia: il verde
                      diceva il contrario di quello che dice ovunque nel resto
                      del prodotto, dove `badge--open` è "fatto, in regola" e
                      `badge--closed` è "manca". Chi passa dal pannello del
                      fornitore a questa schermata leggeva il verde come "a
                      posto" e sarebbe passato oltre proprio alla riga da
                      aprire.
                    */}
                    <span className={thread.claimed ? 'badge badge--open' : 'badge badge--closed'}>
                      {thread.claimed ? t('supervision.claimed') : t('supervision.to_reread')}
                    </span>
                    <button
                      type="button"
                      className="button"
                      onClick={() => void open(thread.sessionId)}
                    >
                      {t('supervision.open_thread')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {/*
        Dichiararsi reperibili.

        Senza questa sezione il pulsante "Allerta supervisore" non troverebbe
        mai nessuno, e il caso che la sezione 5.9 chiede di progettare con più
        cura — "nessun supervisore" — sarebbe l'unico caso esistente.
      */}
      {isSupervisor && !openThread ? (
        <section className="card" aria-labelledby="on-call-heading">
          <h3 id="on-call-heading">{t('on_call.title')}</h3>
          <p className="card__sub">{t('on_call.description')}</p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              void declareOnCall();
            }}
          >
            <div className="field">
              <label htmlFor="on-call-from">{t('on_call.from')}</label>
              <input
                id="on-call-from"
                type="datetime-local"
                value={windowStart}
                onChange={(event) => setWindowStart(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="on-call-to">{t('on_call.to')}</label>
              <input
                id="on-call-to"
                type="datetime-local"
                value={windowEnd}
                onChange={(event) => setWindowEnd(event.target.value)}
              />
            </div>
            {/*
              Non primaria: l'accento di questa schermata è la presa visione del
              protocollo, senza la quale non si entra in turno. Due accenti su
              due schede diverse sono due schermate che si contendono la stessa
              attenzione.
            */}
            <button
              type="submit"
              className="button button--wide"
              disabled={savingWindow || !windowStart || !windowEnd}
            >
              {t(savingWindow ? 'on_call.adding' : 'on_call.add')}
            </button>
          </form>

          <h4>{t('on_call.mine')}</h4>
          {myOnCall === undefined ? (
            <span className="skeleton skeleton--wide" aria-hidden="true" />
          ) : myOnCall.length === 0 ? (
            <p className="muted">{t('on_call.mine_empty')}</p>
          ) : (
            <ul className="plain-list">
              {myOnCall.map((window) => (
                <li key={window.id} className="row">
                  <p>
                    {t('on_call.window', {
                      from: formatWhen(
                        window.startsAt,
                        i18n.resolvedLanguage,
                        user.tenant?.timezone,
                      ),
                      to: formatWhen(window.endsAt, i18n.resolvedLanguage, user.tenant?.timezone),
                    })}
                  </p>
                  <div className="row__actions">
                    <button
                      type="button"
                      className="button"
                      onClick={() => void dropOnCall(window)}
                    >
                      {new Date(window.startsAt).getTime() <= Date.now()
                        ? t('on_call.end')
                        : t('on_call.remove', {
                            when: formatWhen(
                              window.startsAt,
                              i18n.resolvedLanguage,
                              user.tenant?.timezone,
                            ),
                          })}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Chi copre cosa: la domanda del coordinamento (mock 3). */}
          {user.role === 'coordinator' ? (
            <>
              <h4>{t('on_call.coverage')}</h4>
              {coverage === undefined ? (
                <span className="skeleton skeleton--wide" aria-hidden="true" />
              ) : coverage.length === 0 ? (
                <p className="banner banner--warn">{t('on_call.coverage_empty')}</p>
              ) : (
                <ul className="plain-list">
                  {coverage.map((entry) => (
                    <li key={`${entry.startsAt}-${entry.displayName ?? ''}`} className="row">
                      <p className="row__title">{entry.displayName ?? ''}</p>
                      <p className="muted">
                        {t('on_call.window', {
                          from: formatWhen(
                            entry.startsAt,
                            i18n.resolvedLanguage,
                            user.tenant?.timezone,
                          ),
                          to: formatWhen(
                            entry.endsAt,
                            i18n.resolvedLanguage,
                            user.tenant?.timezone,
                          ),
                        })}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </section>
      ) : null}

      <ProtocolSection
        state={protocol}
        /*
          Si comincia a scrivere nella lingua dell'**ente**, non in quella della
          console: senza almeno quella il salvataggio viene rifiutato, e un
          coordinatore con la console in inglese su un ente italiano prendeva un
          errore al primo tentativo.
        */
        defaultLocale={tenantLocale}
        onSaved={() => void loadProtocol()}
        onNotice={say}
      />
    </main>
  );
}

/**
 * Il protocollo: in lettura per tutti, modificabile da chi coordina.
 *
 * Salvare crea una **versione nuova** e rifà la domanda a tutti: la risposta
 * dice a quante persone, perché è la conseguenza che chi salva non si aspetta
 * (AC8, come i turni accorciati dagli orari in S4).
 */
function ProtocolSection({
  state,
  defaultLocale,
  onSaved,
  onNotice,
}: {
  state: ProtocolState | undefined;
  defaultLocale: string;
  onSaved: () => void;
  /**
   * L'esito, con il suo tono: gli altri fallimenti di questa schermata sono
   * rossi, e due fallimenti che si vedono diversi nella stessa pagina sono una
   * differenza che non vuol dire niente.
   */
  onNotice: (message: string, tone?: Tone) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [locale, setLocale] = useState(defaultLocale);
  const [steps, setSteps] = useState<ProtocolSteps>({});
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const startEditing = (): void => {
    setSteps(cloneSteps(state?.protocol?.stepsByLocale ?? {}));
    setPhone(state?.protocol?.onCallPhone ?? '');
    setEditing(true);
  };

  const current = steps[locale] ?? [];

  const save = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await saveCrisisProtocol({
        steps,
        onCallPhone: phone.trim() === '' ? null : phone.trim(),
      });
      onNotice(
        `${t('protocol.saved')} ${t(
          result.acksReset === 1 ? 'protocol.saved_acks_one' : 'protocol.saved_acks_other',
          { count: result.acksReset },
        )}`,
      );
      setEditing(false);
      onSaved();
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      onNotice(`${t('protocol.save_failed')} ${apiMessage(t, key)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (): Promise<void> => {
    try {
      await acknowledgeProtocol();
      onNotice(t('protocol.acked'));
      onSaved();
    } catch {
      onNotice(t('protocol.ack_failed'), 'danger');
    }
  };

  return (
    <section className="card" aria-labelledby="protocol-heading">
      <h3 id="protocol-heading">{t('protocol.title')}</h3>
      <p className="card__sub">{t('protocol.description')}</p>

      {!state ? (
        <>
          <p className="visually-hidden">{t('moderation.loading')}</p>
          <span className="skeleton skeleton--wide" aria-hidden="true" />
          <span className="skeleton skeleton--half" aria-hidden="true" />
        </>
      ) : null}

      {state && !state.protocol ? (
        <p className="banner banner--warn">
          {state.editable ? t('protocol.none_coordinator') : t('protocol.none')}
        </p>
      ) : null}

      {state?.protocol && !editing ? (
        <>
          <p className="muted">
            {`${t('protocol.version', { version: state.protocol.version })} · ${t(
              'protocol.updated',
              { when: state.protocol.updatedAt.slice(0, 10) },
            )}`}
          </p>
          <ol className="protocol-steps">
            {state.protocol.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p className="muted">
            {state.protocol.onCallPhone
              ? t('protocol.on_call_call', { number: state.protocol.onCallPhone })
              : t('protocol.on_call_none')}
          </p>
          {!state.acknowledged ? (
            <button
              type="button"
              className="button button--primary button--wide"
              onClick={() => void acknowledge()}
            >
              {t('protocol.ack')}
            </button>
          ) : null}
        </>
      ) : null}

      {state?.editable && !editing ? (
        <button type="button" className="button" onClick={startEditing}>
          {t('protocol.edit')}
        </button>
      ) : null}

      {editing ? (
        <>
          <p className="banner banner--warn">{t('protocol.new_version_note')}</p>

          <div className="field">
            <label htmlFor="protocol-locale">{t('protocol.locale_label')}</label>
            {/*
              Le lingue sono quelle che il prodotto sa mostrare, non due scritte
              a mano: aggiungerne una terza non deve richiedere di ricordarsi di
              questa riga.
            */}
            <select
              id="protocol-locale"
              value={locale}
              onChange={(event) => setLocale(event.target.value)}
            >
              {SUPPORTED_LOCALES.map((code) => (
                <option key={code} value={code}>
                  {t(`language.${code}`)}
                </option>
              ))}
            </select>
          </div>

          {/*
            Un gruppo di campi, non il selettore di presenza.

            Era `fieldset className="presence"`, cioè il vestito di un
            componente disegnato per tre opzioni colorate, addosso a un elenco
            di caselle di testo: la legenda prendeva il peso del selettore e i
            passi non avevano nessun contenitore visibile.
          */}
          <fieldset className="field-group">
            <legend>{t('protocol.steps_label')}</legend>
            {current.map((step, index) => (
              /*
                Il passo e il pulsante che lo toglie sulla **stessa riga**.

                Erano incolonnati — campo, "Togli il passo 1", campo, "Togli il
                passo 2" — e con sei passi facevano dodici controlli alternati
                senza nessun raggruppamento, in cui non si capiva se un
                pulsante appartenesse al campo sopra o a quello sotto. A schermo
                resta la parola breve; il nome accessibile è **identico** a
                prima, "Togli il passo N", perché è l'unica cosa che distingue
                sei pulsanti uguali per chi ascolta la pagina.
              */
              // biome-ignore lint/suspicious/noArrayIndexKey: i passi sono una sequenza ordinata modificabile, e due passi possono avere lo stesso testo.
              <div className="step-row" key={`${locale}-${index}`}>
                <div className="field">
                  <label htmlFor={`protocol-step-${index}`}>
                    {t('protocol.step_label', { index: index + 1 })}
                  </label>
                  <input
                    id={`protocol-step-${index}`}
                    type="text"
                    maxLength={300}
                    value={step}
                    onChange={(event) =>
                      setSteps((previous) => withStep(previous, locale, index, event.target.value))
                    }
                  />
                </div>
                <button
                  type="button"
                  className="button button--danger"
                  aria-label={t('protocol.remove_step', { index: index + 1 })}
                  onClick={() => setSteps((previous) => withoutStep(previous, locale, index))}
                >
                  {t('protocol.remove_short')}
                </button>
              </div>
            ))}
            <button
              type="button"
              className="button"
              onClick={() => setSteps((previous) => withStep(previous, locale, current.length, ''))}
            >
              {t('protocol.add_step')}
            </button>
          </fieldset>

          <div className="field">
            <label htmlFor="protocol-phone">{t('protocol.on_call_label')}</label>
            <input
              id="protocol-phone"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              aria-describedby="protocol-phone-hint"
            />
            <p className="muted" id="protocol-phone-hint">
              {t('protocol.on_call_hint')}
            </p>
          </div>

          {/* Due uscite, e si distinguono: quella che salva e quella che
              rinuncia. Erano due pulsanti larghi quanto lo schermo, uno sotto
              l'altro, cioè due uscite dello stesso peso. */}
          <div className="actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void save()}
              disabled={busy}
            >
              {t(busy ? 'protocol.saving' : 'protocol.save')}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setEditing(false)}
            >
              {t('report_user.cancel')}
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
